import { FC } from 'react';
import { Avatar as _Avatar, Grid } from '@material-ui/core';
import BusinessIcon from '@material-ui/icons/Business';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import _ChatBubbleIcon from '@material-ui/icons/ChatBubble';
import _ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import _GetAppIcon from '@material-ui/icons/GetApp';
import styled from '@emotion/styled';
import { useQuery } from '@apollo/client';

import {
  GetVariationVersionsDocument,
  GetVariationVersionsQuery,
  GetVariationVersionsQueryVariables,
  Project,
  Variation,
  Version,
} from '../../../../generated/graphql';
import { PaperContainer } from '../../primitive/Paper';
import Spinner from '../../primitive/Spinner';
import { COLOR_TEXT_MAIN } from '../../../../theme/colors';
import { SecondaryButton } from './common';
import {
  SectionTitle as _SectionTitle,
  SectionSubTitle as _SectionSubTitle,
  BodyBold as _BodyBold,
  BodySmall,
  LabelExtraSmall,
} from '../../primitive/TypographyUI';
import { convertJST } from '../../../../utility/convertJST';
import { getURLWithQueryAdd } from '../../../../utility/getURLWithQueryAdd';
import { downloadFile as onClickDownload } from '../../../../utility/downloadFile';
import { usePreview } from '../../../../hooks/usePreview';

type Props = {
  project: Project;
  variation: Variation;
};

const VersionHistory: FC<Props> = ({ project, variation }) => {
  const { getPreview } = usePreview({ variation, projectType: project.project_type });
  const { data: { versions } = { versions: null }, loading: versionLoading } = useQuery<
    GetVariationVersionsQuery,
    GetVariationVersionsQueryVariables
  >(GetVariationVersionsDocument, {
    variables: { variation_id: variation.id },
    context: { clientName: 'planbcd' },
  });

  const onClickPreview = (version: Version) => {
    getPreview({ version: version.version_num }).then((data) => {
      const { generatePreview: preview } = data!;
      if (project.project_type === 'redirect_test') {
        window.open(version.redirect_url, '', '');
      } else if (preview?.query_string && project?.url) {
        window.open(`${getURLWithQueryAdd(project.url, preview.query_string)}`, '', '');
      }
    });
  };

  if (versionLoading)
    return (
      <PaperContainer>
        <Spinner />
      </PaperContainer>
    );

  if (!versions || !variation.permissions?.show_versions?.permit)
    return (
      <PaperContainer>
        <BodySmall style={{ color: '#BF4140' }}>「編集履歴」を参照する権限がありません。</BodySmall>
      </PaperContainer>
    );

  return (
    <PaperContainer>
      <SectionTitle>編集履歴</SectionTitle>
      <BodySmall>本デザイン案の編集履歴です。</BodySmall>
      <BodySmall>デザイン案の編集投稿毎に履歴が保存されます。</BodySmall>
      <BodySmall>各編集履歴よりプレビューおよび複製などのアクションメニューが実施できます。</BodySmall>
      <SectionSubTitle>編集履歴一覧</SectionSubTitle>
      <ColumnFlexWrapper>
        {versions.map((version, i) => {
          const text =
            variation?.applicable_version_id === version.id
              ? '反映待ち'
              : version?.submitted_at === null
              ? '作成中'
              : version.version_num === variation.version_num
              ? '使用中'
              : '過去のバージョン';
          const BoldOrSmall = i === 0 ? _BodyBold : BodySmall;
          return (
            <RelativeAnchor key={i}>
              <StyledPaperInner>
                <FirstColumn item>
                  <BodyExtraSmall>更新日</BodyExtraSmall>
                  <BoldOrSmall>{`${convertJST(version.submitted_at || version.updated_at)}`}</BoldOrSmall>
                </FirstColumn>
                <Column item>
                  <BodyExtraSmall>ステータス</BodyExtraSmall>
                  <BoldOrSmall>{text}</BoldOrSmall>
                </Column>
                <Column item>
                  <BodyExtraSmall>更新者</BodyExtraSmall>
                  <Grid container alignItems="center" wrap={'nowrap'}>
                    {version?.user?.avatar_image_url ? (
                      <>
                        <Avatar src={version?.user?.avatar_image_url} />
                        <span>{version?.user?.username || ''}</span>
                      </>
                    ) : variation?.plan_a ? (
                      <>
                        <Avatar>
                          <BusinessIcon />
                        </Avatar>
                        <span>{version?.user?.username || ''}</span>
                      </>
                    ) : (
                      <>
                        <Avatar>
                          <AccountCircleIcon />
                        </Avatar>
                        <span>{version?.user?.username || ''}</span>
                      </>
                    )}
                  </Grid>
                </Column>
                <Column item alignItems={'center'}>
                  <BodyExtraSmall>変更意図</BodyExtraSmall>
                  <CommentChatBubbleIcon {...variation} />
                </Column>
                <Column item>
                  <SecondaryButton onClick={() => onClickPreview(version)}>編集履歴のプレビュー</SecondaryButton>
                </Column>
                <Column item>
                  <SecondaryButton onClick={() => onClickDownload(version)}>
                    ダウンロード
                    <GetAppIcon />
                  </SecondaryButton>
                </Column>
              </StyledPaperInner>
            </RelativeAnchor>
          );
        })}
      </ColumnFlexWrapper>
    </PaperContainer>
  );
};

// TODO: avoid using styles
export const CommentChatBubbleIcon = ({ submit_comment, submit_comment_changes }: Variation) =>
  submit_comment || submit_comment_changes ? (
    <Element>
      <ChatBubbleIcon />
      <Pop>
        {submit_comment_changes && (
          <>
            <LabelExtraSmallWhite>デザイン案の変更箇所</LabelExtraSmallWhite>
            <BodySmallWhite style={{ marginBottom: '16px' }}>{submit_comment_changes || '記入なし'}</BodySmallWhite>
          </>
        )}
        {submit_comment && (
          <>
            <LabelExtraSmallWhite>デザインの変更意図</LabelExtraSmallWhite>
            <BodySmallWhite>{submit_comment || '記入なし'}</BodySmallWhite>
          </>
        )}
      </Pop>
    </Element>
  ) : (
    <ChatBubbleOutlineIcon />
  );

const Element = styled('div')(`
  :hover > div {
    display: block;
  }
`);

const Pop = styled('div')(`
  position: absolute;
  top: 4rem;
  left: calc(100% / 2 - 1rem);
  z-index: 1;
  line-height: 22px;
  color: #fff;
  text-align: left;
  border-radius: 8px;
  width: 300px;
  padding: 24px;
  font-size: 14px;
  background-color: #4C566CEE; 
  box-shadow: 0px 1px 9px rgba(0, 0, 0, 0.04);
  border: 1px solid #999;
  display: none;
`);

const ChatBubbleIcon = styled(_ChatBubbleIcon)(`
  cursor: pointer;
  color: ${COLOR_TEXT_MAIN};
`);

const ChatBubbleOutlineIcon = styled(_ChatBubbleOutlineIcon)(`
  color: ${COLOR_TEXT_MAIN};
`);

/* ------ */

const GetAppIcon = styled(_GetAppIcon)(`color: ${COLOR_TEXT_MAIN};`);

const SectionTitle = styled(_SectionTitle)(`
  margin-bottom: 1rem;
`);

const SectionSubTitle = styled(_SectionSubTitle)(`
  margin: 2rem 0 1rem;
`);

const BodySmallWhite = styled(BodySmall)(`
  white-space: pre-wrap;
  color: #fff;
  margin-bottom: 1rem;
`);

const BodyExtraSmall = styled(BodySmall)(`
  font-size: 12px;
`);

const LabelExtraSmallWhite = styled(LabelExtraSmall)(`
  margin: 0.4rem 0 0.6rem;
  color: #fff;
`);

const ColumnFlexWrapper = styled('div')(`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 1rem;
  padding-bottom: 4rem;
`);

//HINT: In order to override direct parent's overflow: scroll for "Pop", child needs ancestor to have "relative"
const RelativeAnchor = styled('div')(`
  position: relative;
`);

const StyledPaperInner = styled('div')(`
  width: 100%;
  padding: 12px 16px;
  height: 80px;
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: spece-between;
  gap: 1rem;
  background: #F5F6F7;
  box-shadow: 0px 1px 9px rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  overflow-x: scroll;
`);

const Column = styled(Grid)(`
  flex: 1 0 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  white-space: nowrap;
`);

const FirstColumn = styled(Column)(`
  flex-grow: 2;
`);

const Avatar = styled(_Avatar)(`
  width: 24px;
  height: 24px;
  margin-right: 0.4rem;
`);

export default VersionHistory;
